export const SET_MEASURE_SELECTED = 'Measures.SET_MEASURE_SELECTED';
export const SET_MEASURE_NAME = 'Measures.SET_MEASURE_NAME';
export const SET_OVERLAY = 'Measures.SET_OVERLAY';
export const SHOW_TABLE = 'Measures.SHOW_TABLE';
export const SET_TABLE_NAME = 'Measures.SET_TABLE_NAME';
export const MEASURE_TYPE = 'Measures.MEASURE_TYPE';
export const SET_NEW_MEASURE = 'Measures.SET_NEW_MEASURE';
export const SET_SCORES_METHOD = 'Measures.SET_SCORES_METHOD';
export const SET_ALL_VALUES_ENTERED = 'Measures.SET_ALL_VALUES_ENTERED';
export const SET_SCORES = 'Measures.SET_SCORES';
export const SET_SCORES_SUBCATEGORY = 'Measures.SET_SCORES_SUBCATEGORY';
export const RESET_SCORES = 'Measures.RESET_SCORES';
export const SET_LOADER = 'Measures.SET_LOADER';
export const SET_EDITING_MEASURE = 'Measures.SET_EDITING_MEASURE';

export const vitalsType: mumms.measureTypeNames = 'vitals';
export const scoresType: mumms.measureTypeNames = 'esas';
export const adlsType: mumms.measureTypeNames = 'adls';
export const pcFocusType: mumms.measureTypeNames = 'pcfocus';
export const otherType: mumms.measureTypeNames = 'other';

/** Legacy category shortnames before OP-29221 metadata. */
export const limitationsType: mumms.measureTypeNames = 'limitations';
export const circumferenceType: mumms.measureTypeNames = 'circ';

export const measureCategoryTypes: mumms.measureTypeNames[] = [
  vitalsType,
  scoresType,
  adlsType,
  pcFocusType,
  otherType,
];

export const measureCategoryLabels: Record<string, string> = {
  vitals: 'Vitals',
  esas: 'ESAS',
  adls: 'ADLs',
  pcfocus: 'PC Focus',
  other: 'Other',
  limitations: 'ADLs',
  circ: 'PC Focus',
  misc: 'Other',
  scores: 'ESAS',
};

const legacyMeasureTypeMap: Record<string, mumms.measureTypeNames> = {
  circ: pcFocusType,
  limitations: adlsType,
  misc: otherType,
  scores: scoresType,
};

export const normalizeStoredMeasureType = (
  stored: string | null,
): mumms.measureTypeNames => {
  if (!stored) {
    return vitalsType;
  }
  const mapped = legacyMeasureTypeMap[stored];
  if (mapped) {
    return mapped;
  }
  if (measureCategoryTypes.includes(stored as mumms.measureTypeNames)) {
    return stored as mumms.measureTypeNames;
  }
  return vitalsType;
};

export const getMeasureCategoryOptions = (): mumms.OptionType[] =>
  measureCategoryTypes.map((value) => ({
    label: measureCategoryLabels[value],
    value,
  }));

/**
 * Shortnames that were selectable in the pre–OP-28847 UI (vitals | esas |
 * circ | limitations tabs). Anything else the API returns was historically
 * hidden by category (e.g. misc growth, labs). Height is included: it is a vital.
 */
export const preexistingVisibleMeasureShortnames = [
  'bp',
  'pulse',
  'temp',
  'resp',
  'oxygen-sat',
  'weight',
  'height',
  'bmi',
  'map',
  'pain',
  'tiredness',
  'nausea',
  'depression',
  'anxiety',
  'drowsiness',
  'appetite',
  'wellbeing',
  'dyspnea',
  'pps',
  'kps',
  'fast',
  'MAC-L',
  'MAC-R',
  'MTC-L',
  'MTC-R',
  'ABD',
] as const;

/** New measure types introduced with OP-29221 / OP-28847. */
export const op28847NewMeasureShortnames = ['nyha', 'ecog'] as const;

const uiVisibleMeasureShortnameSet = new Set<string>([
  ...preexistingVisibleMeasureShortnames,
  ...op28847NewMeasureShortnames,
]);

export const isUiVisibleMeasureShortname = (shortname: string | undefined) =>
  !!shortname && uiVisibleMeasureShortnameSet.has(shortname);

export const filterUiVisibleMeasureTypes = (
  types: mumms.measureType[] | null | undefined,
): mumms.measureType[] => {
  if (!types) {
    return [];
  }
  return types.filter((type) => isUiVisibleMeasureShortname(type.shortname));
};

/**
 * Sort a measureByType object so categories appear in the same order as
 * measureCategoryTypes (UI tab order) and shortnames within each category
 * follow the order of allMeasuresTypes[category] (backend metadata order).
 */
export const sortedMeasureEntries = (
  measures: mumms.measureByType,
  allMeasuresTypes: mumms.formattedAllTypes | null | undefined,
): [string, mumms.formattedAllMeasures][] => {
  const categoryEntries = Object.entries(measures) as [
    string,
    mumms.formattedAllMeasures,
  ][];
  categoryEntries.sort((a, b) => {
    const ai = measureCategoryTypes.indexOf(a[0] as mumms.measureTypeNames);
    const bi = measureCategoryTypes.indexOf(b[0] as mumms.measureTypeNames);
    return (
      (ai === -1 ? measureCategoryTypes.length : ai) -
      (bi === -1 ? measureCategoryTypes.length : bi)
    );
  });
  if (!allMeasuresTypes) return categoryEntries;
  return categoryEntries.map(([cat, catMeasures]) => {
    const typeOrder = (
      ((allMeasuresTypes as mumms.formattedAllTypes)[
        cat as mumms.measureTypeNames
      ] as mumms.measureType[] | undefined) || []
    ).map((t: mumms.measureType) => t.shortname);
    const shortnameEntries = Object.entries(catMeasures);
    shortnameEntries.sort((a, b) => {
      const ai = typeOrder.indexOf(a[0]);
      const bi = typeOrder.indexOf(b[0]);
      return (
        (ai === -1 ? typeOrder.length : ai) -
        (bi === -1 ? typeOrder.length : bi)
      );
    });
    return [cat, Object.fromEntries(shortnameEntries)] as [
      string,
      mumms.formattedAllMeasures,
    ];
  });
};

/** OP-29221 may omit legacy category keys; always return an array. */
export const getMeasureTypesForCategory = (
  allMeasuresTypes: mumms.formattedAllTypes | null | undefined,
  category: mumms.measureTypeNames,
): mumms.measureType[] => {
  if (!allMeasuresTypes) {
    return [];
  }
  return filterUiVisibleMeasureTypes(
    (allMeasuresTypes as mumms.formattedAllTypes)[category],
  );
};

export const getAllMeasureTypesFlat = (
  allMeasuresTypes: mumms.formattedAllTypes | null | undefined,
): mumms.measureType[] => {
  if (!allMeasuresTypes) {
    return [];
  }
  const flattened = Object.values(allMeasuresTypes).reduce<mumms.measureType[]>(
    (acc, types) =>
      Array.isArray(types) ? acc.concat(types as mumms.measureType[]) : acc,
    [],
  );
  return filterUiVisibleMeasureTypes(flattened);
};

/** Long description measures — taller listing/print rows. */
export const longDescMeasureShortnames = [
  'kps',
  'pps',
  'fast',
  'nyha',
  'ecog',
] as const;

export const isLongDescMeasure = (name: string | undefined) =>
  !!name && (longDescMeasureShortnames as readonly string[]).includes(name);

/** Resolve measure description; safe for shortname "map". */
export const getMeasureDescriptionFromMaps = (
  name: string | undefined,
  measuresMapName: Record<string, string> | null | undefined,
  measureType?: mumms.measureType | null,
): string => {
  if (!name) {
    return '';
  }
  if (
    measuresMapName &&
    Object.prototype.hasOwnProperty.call(measuresMapName, name) &&
    measuresMapName[name]
  ) {
    return measuresMapName[name];
  }
  return measureType?.description ?? measureType?.name ?? '';
};

/** Widget More-table row height for long descriptions, including ECOG 1. */
export const longDescListingRowHeight = 60;

export const patientRefused = 'Patient Refused';
export const notTaken = 'Not Taken';
export const afebrile = 'Afebrile';
// TODO use missingReasons
export const missingReasons = [patientRefused, notTaken, afebrile];

export const editTable = 'editTable';
export const displayTable = 'displayTable';

export const noPost = 'noPost';
export const active = 'active';

export const dropdownFields = ['pps', 'kps', 'fast', 'nyha', 'ecog'];
/** Recent history rows shown in the chart band for non-graphable measures. */
export const nonGraphablePreviewRowCount = 4;
/** Small-widget preview: fewer rows so long ECOG lines fit without colliding. */
export const smallWidgetNonGraphablePreviewRowCount = 2;
export const highChartTimelineHeight = 20;
export const highChartYAxistWidth = 65;
export const nameWidth = 110;
export const chartHeightRatio = 0.5;
export const tableHeightRatio = 0.3;
// to avoid any scrolling
export const smallChartHeightRatio = 0.85;
// name width 110px- why did I leave this comment
export const fullChartWidth = 0.98;
export const marginBottom = 40;
export const tableHeight = 350;

const patientReporting = 'By Patient Reporting';
export const observation = 'By Observation';
export const individually = 'Individually';
export const observationMethods = [patientReporting, observation, individually];
export enum ObservationMethod {
  PATIENT = 'By Patient Reporting',
  OBSERVATION = 'By Observation',
  INDIVIDUALLY = 'Individually',
}
export const objective = 'objective';
export const subjective = 'subjective';

/** Vitals + limitations display order in Measures widget (OP-32960). Unlisted types sort after. */
export const VITALS_DISPLAY_ORDER: mumms.measureShortNames[] = [
  'height',
  'weight',
  'bmi',
  'oxygen-sat',
  'resp',
  'pulse',
  'bp',
  'map',
  'temp',
  'fast',
  'pps',
  'kps',
];
